import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { AuthScaffold } from './AuthScaffold'
import { FunnelAside } from './FunnelAside'
import { OAuthButtons, OrDivider, useOAuthProviders } from './OAuthButtons'
import {
  arrivedFromFunnel,
  destinationAfterRegister,
  destinationAfterSignIn,
  safeReturnTo,
} from './afterAuth'
import { loginErrorMessage, registerErrorMessage } from './authErrors'
import { usePendingUpload } from './usePendingUpload'
import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxTextField } from '@/components/fx'
import { pendingUpload } from '@/lib/pendingUpload'
import { PRIVACY_URL, TERMS_URL } from '@/lib/siteUrl'

/**
 * Live documents on the marketing site.
 *
 * There is no Auftragsverarbeitungsvertrag to link to yet, so the acceptance
 * does not name one: asking someone to accept an unlinked contract — and then
 * recording nothing, because `POST /v1/auth/register` takes no terms field —
 * is the worst of both. When the AVV exists it belongs here, with a stored
 * acceptance beside it.
 */

export function RegisterPage() {
  const { register, login, signInWithGoogleToken } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { data: providers } = useOAuthProviders()
  const { data: held } = usePendingUpload()

  const next = params.get('next')
  const funnel = arrivedFromFunnel({ pendingUpload: held != null, next })

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' })
  const [accepted, setAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function field(name: keyof typeof form) {
    return {
      value: form[name],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
        setForm((current) => ({ ...current, [name]: event.target.value })),
    }
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await register(form)
    } catch (cause) {
      setError(registerErrorMessage(cause))
      setBusy(false)
      return
    }

    // Registration answers 201 with a session, but `authApi.register` does
    // not keep the token, so sign in with the credentials that were just
    // accepted. Confirming the address is enforced later, by the gateway's
    // grace window — which is what lets the funnel go straight on to the
    // form instead of stopping at an interstitial.
    try {
      await login(form.email, form.password)
    } catch (cause) {
      // The account exists; only the session does not. Say so on the
      // confirmation screen rather than looking like registration failed.
      navigate('/verify-email', {
        replace: true,
        state: { email: form.email, signInFailed: loginErrorMessage(cause) },
      })
      return
    }

    const stillHeld = await pendingUpload.take()
    navigate(destinationAfterRegister({ pendingUpload: stillHeld != null, next }), {
      replace: true,
      state: { email: form.email },
    })
  }

  const returnTo = safeReturnTo(next) ?? undefined

  return (
    <AuthScaffold
      title="Konto anlegen"
      subtitle={
        funnel
          ? 'Danach geht es direkt mit deinem Formular weiter.'
          : '10 Seiten und ein Formular sind kostenlos.'
      }
      align="start"
      width={440}
      logoClassName="h-16 w-16"
      aside={funnel ? <FunnelAside /> : undefined}
      footer={
        <p className="text-small text-text">
          Schon registriert?{' '}
          <Link className="font-semibold text-primary-text" to="/login">
            Anmelden
          </Link>
        </p>
      }
    >
      {error && <FxAlert tone="danger">{error}</FxAlert>}

      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <div className="grid grid-cols-1 gap-4 xs:grid-cols-2">
          <FxTextField
            label="Vorname"
            onWhite
            required
            autoComplete="given-name"
            {...field('firstName')}
          />
          <FxTextField
            label="Nachname"
            onWhite
            autoComplete="family-name"
            {...field('lastName')}
          />
        </div>
        <FxTextField
          label="E-Mail"
          onWhite
          type="email"
          required
          autoComplete="email"
          {...field('email')}
        />
        <FxTextField
          label="Passwort"
          onWhite
          type="password"
          required
          minLength={8}
          autoComplete="new-password"
          hint="Mindestens 8 Zeichen."
          {...field('password')}
        />

        <label className="flex items-start gap-3 text-small leading-relaxed text-text">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5 flex-none accent-primary-strong"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
          />
          <span>
            Ich akzeptiere die{' '}
            <a className="text-primary-text" href={TERMS_URL} target="_blank" rel="noreferrer">
              AGB
            </a>{' '}
            und habe die{' '}
            <a className="text-primary-text" href={PRIVACY_URL} target="_blank" rel="noreferrer">
              Datenschutzerklärung
            </a>{' '}
            zur Kenntnis genommen.
          </span>
        </label>

        <FxButton type="submit" block disabled={busy || !accepted}>
          {busy
            ? 'Konto wird angelegt …'
            : funnel
              ? 'Konto anlegen und testen'
              : 'Konto anlegen'}
        </FxButton>
      </form>

      {providers.length > 0 && (
        <>
          <OrDivider />
          <OAuthButtons
            providers={providers}
            returnTo={returnTo}
            disabled={busy}
            microsoftLabel="Mit Microsoft"
            googleText="signup_with"
            onGoogleCredential={(idToken) => {
              setError(null)
              void (async () => {
                try {
                  await signInWithGoogleToken(idToken)
                  const stillHeld = await pendingUpload.take()
                  // A Google account arrives verified (the gateway copies
                  // the token's `email_verified`), so there is nothing for
                  // the confirmation screen to say.
                  navigate(destinationAfterSignIn({ pendingUpload: stillHeld != null, next }), {
                    replace: true,
                  })
                } catch (cause) {
                  setError(registerErrorMessage(cause))
                }
              })()
            }}
          />
          {providers.includes('google') && (
            <p className="text-small text-text-muted">
              Mit Google entfällt die Bestätigung per E-Mail.
            </p>
          )}
        </>
      )}
    </AuthScaffold>
  )
}
